// Search across Tamil, English and Tanglish; results explain why they matched.
import { useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError, thumbURL } from '../api';
import { Thumb } from '../components/Thumb';
import { Link, navigate } from '../router';
import { Back, Close } from '../icons';
import { Highlight } from '../lib/highlight';
import { useScript } from '../script';
import { namesFor } from '../lib/format';
import { useT } from '../i18n';
import { useToast } from '../components/Shell';
import { SwipeToGrocery } from '../components/Swipe';
import type { Hit } from '../types';

export function Search() {
  const t = useT();
  const [q, setQ] = useState(() => new URLSearchParams(location.search).get('q') || '');
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [err, setErr] = useState('');
  const [toast, showToast] = useToast();
  const [script] = useScript();
  const input = useRef<HTMLInputElement>(null);
  const seq = useRef(0);

  useEffect(() => { input.current?.focus(); }, []);
  useEffect(() => {
    const query = q.trim();
    history.replaceState(null, '', query ? `/search?q=${encodeURIComponent(query)}` : '/search');
    if (!query) { setHits(null); setErr(''); return; }
    const mine = ++seq.current;
    const t = setTimeout(() => {
      api.search(query)
        .then((h) => { if (mine === seq.current) { setHits(h); setErr(''); } })
        .catch((e: ApiError) => { if (mine === seq.current && e.status !== 401) setErr(e.message); });
    }, 250); // debounce typing; ignore responses that arrive out of order
    return () => clearTimeout(t);
  }, [q]);

  // Other-script names from the matches, de-duplicated ignoring case ("Turmeric Powder" = "turmeric powder").
  const seen = new Set<string>([q.trim().toLowerCase()]);
  const also = (hits ?? []).flatMap((h) => h.match?.alt ?? []).filter((a) => {
    const k = a.trim().toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  }).slice(0, 3);

  return (
    <div class="page">
      <div style="padding:12px 12px 0 8px;display:flex;align-items:center;gap:4px">
        <button class="icon-btn" aria-label={t('search.back')} onClick={() => (history.length > 1 ? history.back() : navigate('/'))}><Back /></button>
        <label class="field active" style="flex:1">
          <input ref={input} type="search" enterKeyHint="search" value={q} aria-label={t('search.label')}
            placeholder={t('search.placeholder')} autoComplete="off"
            onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === 'Escape' && navigate('/')} />
          {q && <button class="icon-btn" style="width:40px;height:40px;color:var(--muted)" aria-label={t('search.clear')} onClick={() => { setQ(''); input.current?.focus(); }}><Close size={18} /></button>}
        </label>
      </div>

      {also.length > 0 && (
        <div style="padding:14px 20px 4px;display:flex;flex-wrap:wrap;align-items:center;gap:6px;font-size:14px;color:var(--muted)">
          <span>{t('search.also')}</span>
          {also.map((a) => <span style="padding:2px 10px;border-radius:999px;background:var(--card);border:1px solid var(--line);color:var(--ink);font-weight:600">{a}</span>)}
        </div>
      )}

      {err && <div class="pad error-text" role="alert" style="padding-top:12px">{err}</div>}

      {hits !== null && (
        <div class="display" style="padding:6px 20px 10px;font-weight:700;font-size:18px" role="status">
          {t('search.count', { n: hits.length })}
        </div>
      )}

      <div class="pad list" style="gap:10px">
        {(hits ?? []).map((h) => {
          const [primary, secondary] = namesFor({ ta: h.dish_name_ta, en: h.dish_name_en, tg: h.dish_name_translit }, script);
          return (
            <SwipeToGrocery key={h.reel_id} id={h.reel_id} onToast={showToast}><Link href={`/r/${h.reel_id}`} class="card result" style="border-radius:16px;flex-direction:row;gap:14px;align-items:flex-start">
              <Thumb src={thumbURL(h.reel_id)} name={primary} size="card" />
              <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:4px">
                <div class="display" style="font-weight:700;font-size:20px;line-height:1.4"><Highlight text={primary} q={q} /></div>
                <div class="muted" style="font-size:14px">{[secondary, h.creator_handle && '@' + h.creator_handle].filter(Boolean).join(' · ')}</div>
                <MatchLine h={h} q={q} />
              </span>
            </Link></SwipeToGrocery>
          );
        })}
      </div>

      {(hits === null || hits.length === 0) && (
        <div class="hint-box" style="margin:18px 16px 0">
          {hits === null ? t('search.idle') : t('search.none')} {t('search.tryScripts')}
        </div>
      )}
      {toast}
    </div>
  );
}

function MatchLine({ h, q }: { h: Hit; q: string }) {
  const t = useT();
  const m = h.match;
  if (!m) return null;
  switch (m.kind) {
    case 'dish':
      return <div style="font-size:13px;color:var(--accent-text);font-weight:600">{t('match.dish')}</div>;
    case 'ingredient':
      return (
        <div style="font-size:14px;line-height:1.5">
          {t('match.ingredient')} <Highlight text={m.text || ''} q={q} />
          {m.alt?.length ? ` · ${m.alt.join(' · ')}` : ''}{m.quantity ? ` · ${m.quantity}` : ''}
        </div>
      );
    case 'transcript':
      return <div style="font-size:14px;line-height:1.5">{t('match.transcript')} “<Highlight text={m.text || ''} q={q} />”</div>;
    case 'caption':
      return <div style="font-size:14px;line-height:1.5">{t('match.caption')} “<Highlight text={m.text || ''} q={q} />”</div>;
    case 'tag':
      return <div style="font-size:13px;color:var(--accent-text);font-weight:600">{t('match.tag', { t: m.text ?? '' })}</div>;
    case 'creator':
      return <div style="font-size:13px;color:var(--accent-text);font-weight:600">{t('match.creator', { t: m.text ?? '' })}</div>;
    default:
      return <div style="font-size:13px;color:var(--muted)">{t('match.similar')}</div>;
  }
}
